import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect, useState } from "react";
import axios from "axios";
import Cards from "../components/ServiceCard";
import CounselingFormModal from "../components/CounselingFormModal";
import servicesData from "../json/services.json";
import { getAuthHeaders } from "../utils/authUtils";
import { generatePageNumbers } from "../utils/pagination";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

export const Services = () => {
  const [servicesContent, setServicesContent] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 8,
    total: 0,
    total_pages: 1,
  });

  useEffect(() => {
    document.title = servicesData.title;
    AOS.init({ duration: 800, once: false });
    setServicesContent(servicesData);
    fetchServices();
  }, []);

  const fetchServices = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URI}/entities?category=service`,
        getAuthHeaders(),
      );

      if (res.data && res.data.data) {
        const transformedData = res.data.data.map((item) => ({
          id: item.id,
          image:
            item.data.images && item.data.images.length > 0
              ? item.data.images[0]
              : null,
          title: item.data.title,
          description: item.data.description,
          button_text: item.data.button_text,
        }));

        setServices(transformedData);
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      } else {
        setServices([]);
      }
      setError("");
    } catch (err) {
      setError("Failed to load services. Showing default content.");
      if (servicesData && servicesData.services) {
        setServices(servicesData.services);
        setPagination({
          page: 1,
          page_size: 8,
          total: servicesData.services.length,
          total_pages: Math.ceil(servicesData.services.length / 8),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && !loading) {
      fetchServices(newPage);
      document
        .querySelector(".services")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const ServiceCardSkeleton = () => (
    <div className="col-12 col-sm-6 col-md-4 mb-4 main-card-box">
      <div
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "20px",
          height: "100%",
          width: "100%",
          background: "#fff",
        }}
      >
        {[200, 24, 16, 16, 16, 36].map((h, i) => (
          <div
            key={i}
            style={{
              width:
                i === 0 ? "100%" : i === 1 ? "80%" : i === 5 ? "100px" : "100%",
              height: `${h}px`,
              borderRadius: "4px",
              marginBottom: i === 0 ? "15px" : "8px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
        ))}
      </div>
    </div>
  );

  if (!servicesContent) return null;

  return (
    <>
      <div className="ss-inner-hero">
        <div className="container">
          <h1>Our Services</h1>
          <p>Expert guidance for every step of your academic journey</p>
        </div>
      </div>
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>

      <section className="services">
        <div className="container">
          {error && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}

          <div className="row justify-content-center mt-5">
            {loading && services.length === 0 && (
              <>
                {Array.from({ length: 8 }).map((_, i) => (
                  <ServiceCardSkeleton key={i} />
                ))}
              </>
            )}
            {loading && services.length > 0 && (
              <>
                {Array.from({ length: pagination.page_size }).map((_, i) => (
                  <ServiceCardSkeleton key={i} />
                ))}
              </>
            )}
            {!loading && services.length === 0 && (
              <div className="col-12 text-center" style={{ padding: "50px" }}>
                <h5>No services available</h5>
                <p>Check back later for new services!</p>
                <button
                  className="btn btn-outline-primary mt-2"
                  onClick={() => fetchServices(1)}
                >
                  Try Again
                </button>
              </div>
            )}
            {!loading &&
              services.map((service) => (
                <div
                  key={service.id}
                  className="col-12 col-sm-6 col-md-4 mb-4 main-card-box"
                >
                  <Cards
                    title={service.title}
                    description={service.description}
                    image={service.image}
                    buttonText={service.button_text}
                    onGetQuote={() => setShowModal(true)}
                  />
                </div>
              ))}
          </div>

          {pagination.total_pages > 1 && (
            <div style={{ textAlign: "center", marginTop: "40px" }}>
              <nav aria-label="Services pagination">
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${
                      pagination.page === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || loading}
                    >
                      Previous
                    </button>
                  </li>
                  {generatePageNumbers(
                    pagination.page,
                    pagination.total_pages,
                  ).map((page, i) => (
                    <li
                      key={i}
                      className={`page-item ${
                        page === pagination.page ? "active" : ""
                      } ${page === "..." ? "disabled" : ""}`}
                    >
                      {page === "..." ? (
                        <span className="page-link">...</span>
                      ) : (
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </button>
                      )}
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      pagination.page === pagination.total_pages
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={
                        pagination.page === pagination.total_pages || loading
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </section>

      <section className="book-free-counseling">
        <div className="container">
          <div className="row">
            <div className="col">
              <div className="bfc-top d-flex justify-content-center">
                <div className="image-container">
                  <div className="logo-cont">
                    <i className="bi bi-telephone"></i>
                  </div>
                  <div className="image">
                    <img
                      src={servicesContent.counselingSection.image}
                      alt="Counseling"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              <div className="bfc-heading mt-4 text-center">
                <h2
                  dangerouslySetInnerHTML={{
                    __html: servicesContent.counselingSection.heading,
                  }}
                ></h2>
              </div>
              <div className="bfc-supporting-text mt-3 text-center">
                <p>{servicesContent.counselingSection.text}</p>
              </div>
              <div className="bfc-buttons text-center mt-5">
                <button
                  type="button"
                  className="btn btn-primary bfc-btn"
                  onClick={() => setShowModal(true)}
                >
                  {servicesContent.counselingSection.button}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CounselingFormModal
        title="Send us a message"
        open={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};
